import { yupResolver } from "@hookform/resolvers/yup";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { BuildingForm, buildingSchema } from "constants/yup.schema";
import { AppDispatch, RootState } from "redux/store";
import { createBuilding } from "../../../redux/slices/buildingsSlice";
import Form from "@/components/form/Form";
import { BUILDINGS_FORM } from "constants/building.form.constants";
import { getAuthTokenData } from "utils/tokenUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import InputField from "@/components/inputField/InputField";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TiDelete } from "react-icons/ti";
type StateProps = {
    initial: string;
    count: number;
    floorNumber: number;
    floortype: string;
};
type floorPlanProps = Array<{
    initial: string;
    count: number;
    floorNumber: number;
    floortype: string;
}>;

export default function AddBuilding() {
    const dispatch = useDispatch<AppDispatch>();
    const { userId, role } = getAuthTokenData();
    const [floorPlan, setFloorPlan] = useState<floorPlanProps>([]);
    const [state, setState] = useState<StateProps>({
        initial: '',
        count: 1,
        floorNumber: 1,
        floortype: 'flats',
    });
    const { remainingLicenses, totalFlats, licenses } = useSelector((state: RootState) => state.admin);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setState({
            ...state,
            [name]: name === "count" || name === "floorNumber" ? Number(value) : value,
        });
    };



    const defaultValues = useRef({
        name: "",
        location: {
            address: "",
            city: "",
            state: "",
            country: "",
        },
    });
    const methods = useForm<BuildingForm>({
        defaultValues: defaultValues.current,
        resolver: yupResolver(buildingSchema),
    });
    const {
        handleSubmit,
        reset: handleReset,
        formState: { isSubmitting },
    } = methods;

    const onSubmit = async (data: any) => {
        const { name, location, type, } = data;
        const totalCount = floorPlan
            .filter(plan => plan.floortype === 'flat') // Only keep items with floortype 'flat'
            .reduce((sum, plan) => sum + plan.count, 0);
        if (totalCount > Number(remainingLicenses)) {
            return toast.error("You can't add more flats.Contact your license provider to increase your license");
        }

        const buildingInfo = {
            name,
            location,
            type,
            floorPlan
        };
        try {

            const newBuilding = { ...buildingInfo, ownerId: userId };
            await dispatch(createBuilding(newBuilding)).unwrap();
            setFloorPlan([]);
            toast.success("Building created successfully!");

        } catch (error: any) {
            toast.error(
                `Failed to create building: ${error.message}`
            );
        } finally {
            handleReset();
        }
    };
    const handleAddFloor = () => {

        setFloorPlan([...floorPlan, state]);
        setState({
            initial: '',
            count: 1,
            floorNumber: 1,
            floortype: 'flats',
        });
    }
    return (
        <>
            {/* <button
                onClick={handleModal}
                className="px-4 py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 transition"
            >
                {"Add New Building"
                }
            </button> */}
            <p className="text-xl font-semibold text-gray-800 my-6">Create New Building</p>
            <Card className="p-5">
                <p className="text-xl font-semibold text-gray-800 ">Floor Information</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
                    {floorPlan.map((floor: any, index: number) => (
                        <Card key={index} className="flex justify-around  items-center">
                            <p className="text-gray-600 m-1">initial: {floor.initial}</p>
                            <p className="text-gray-600 m-1"> count: {floor.count}</p>
                            <p className="text-gray-600 m-1"> floorNumber: {floor.floorNumber}</p>
                            <p className="text-gray-600 m-1"> floorType: {floor.floortype}</p>
                            <TiDelete className="w-8 h-8 text-red-500 hover:text-red-600 transition cursor-pointer" onClick={() => {
                                const updatedFloorPlan = floorPlan.filter((_: any, i: number) => i !== index);
                                setFloorPlan(updatedFloorPlan);
                            }} />
                        </Card>

                    ))}

                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
                    <div>
                        <label className="block mb-2 font-medium">Initial Letter</label>
                        <InputField
                            type="text"
                            name="initial"
                            placeholder="Initial Letter"
                            required={true}
                            onChange={handleChange}
                            value={state.initial}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 font-medium">Count</label>
                        <InputField
                            type="number"
                            name="count"
                            placeholder="Floor count"
                            required={true}
                            onChange={handleChange}
                            value={state.count.toString()}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 font-medium">Floor Number</label>
                        <InputField
                            type="number"
                            name="floorNumber"
                            placeholder="Floor Number"
                            required={true}
                            onChange={handleChange}
                            value={state.floorNumber.toString()}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 font-medium">Floor Type</label>
                        <Select
                            onValueChange={(value) =>
                                setState((prevState: any) => ({ ...prevState, floortype: value }))
                            }
                            value={state.floortype}
                            name="floortype"
                        >
                            <SelectTrigger className="w-full flex-1 col-span-2">
                                <SelectValue placeholder="Select Floor Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem value="flat">Flat</SelectItem>
                                    <SelectItem value="parking">Parking</SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <Button 
                    variant="default" 
                    onClick={handleAddFloor}
                    disabled={!state.initial || !state.floortype }
                >
                    Generate Floor
                </Button>
            </Card>
            <Card className="p-5 col-span-7">

                <Form
                    fields={BUILDINGS_FORM}
                    control={methods.control}
                    method={methods}
                    onClick={handleSubmit(onSubmit)}
                    errors={methods.formState.errors}
                    isSubmitting={isSubmitting}
                    style="grid grid-cols-2 gap-4"
                />
            </Card>
            {/* <Modal
                isModalOpen={edit ? true : isModalOpen}
                onClick={handleModal}
                title={isEdit ? "Edit Building" : "Add a New Building"}
            >
            </Modal> */}
        </>
    );
};
